import { MIN_PULSE_RADIUS } from 'shared/map-search/constants/map';
import { EClustersType } from 'shared/map-search/types/clustersType';
import { TFeature } from 'shared/map-search/types/map';

import { PulseAnimator, PulseDrawCallback } from './PulseAnimator';
import { getPointConfig, IPointConfig } from '../PointsLayer/pointTypes';

import type { TPromoPinConfig } from './types';
import type { IDrawTileParameters } from '../PointsLayer/pointTile';

type TDrawTileWithPulse = {
  tilePixelCoords: [number, number];
  getFeatureTileCoords: (feature: TFeature, tilePixelCoords: [number, number], tileZoom: number) => [number, number];
  canvasContext: CanvasRenderingContext2D;
  drawPoint: (params: IDrawTileParameters) => void;
  pulseProgress?: number;
  isDetailed: boolean;
  isResultable: boolean;
  showOnlyPrice: boolean;
  pointType: EClustersType | null;
};

export class PinPulseManager extends PulseAnimator {
  /** текущий зум карты */
  private tileZoom = 0;
  /** коэффициент устройства (для Retina экранов) */
  private dpr = 1;
  /** контекст канваса, на котором рисуются пульсации */
  private canvasContext: CanvasRenderingContext2D | undefined;
  /** массив объектов(пины), которые нужно отрисовать */
  private features: TFeature[] = [];
  /** Конфигурация пульсации пинов */
  private promoPinConfig: TPromoPinConfig;

  public constructor(draw: PulseDrawCallback, promoPinConfig: TPromoPinConfig) {
    super(draw, promoPinConfig?.duration);
    this.promoPinConfig = promoPinConfig;
  }

  /**
   * Обновление состояния пульсации
   * @param features - массив объектов, которые нужно отрисовать
   * @param tileZoom - текущий зум карты
   * @param dpr - коэффициент устройства (для Retina экранов)
   * @param ctx - контекст канваса, на котором рисуются пульсации
   */
  public updateState(features: TFeature[], tileZoom: number, dpr: number, ctx: CanvasRenderingContext2D | undefined) {
    this.tileZoom = tileZoom;
    this.dpr = dpr;
    this.canvasContext = ctx;
    this.features = features;

    if (this.shouldAnimate()) {
      this.startAnimation();
    } else {
      this.stopAnimation();
    }
  }

  private shouldAnimate() {
    return this.tileZoom >= this.promoPinConfig.activeZoomPin;
  }

  public shouldPulse(feature: TFeature): boolean {
    return this.tileZoom >= this.promoPinConfig.activeZoomPin && !feature.properties.isViewed;
  }

  public drawPulse({
    pointX,
    pointY,
    config: { radius, glowPulsed, glowPulsedColor },
    pulseProgress,
  }: {
    pointX: number;
    pointY: number;
    config: IPointConfig;
    pulseProgress?: number;
  }) {
    if (!this.canvasContext) {
      return;
    }

    if (!glowPulsed || typeof pulseProgress !== 'number') {
      return;
    }

    /** Коэффициент устройства, используется для корректной отрисовки на Retina экранах */
    const dpr = this.dpr;
    /** Максимальный радиус пульсации, который будет равен (radius + MAX_PULSE_RADIUS) * dpr */
    const maxPulse = (radius + this.promoPinConfig.pulseRadius) * dpr;
    /** Минимальный радиус пульсации, который будет равен (radius + MIN_PULSE_RADIUS) * dpr */
    const minPulse = (radius + MIN_PULSE_RADIUS) * dpr;
    /** Радиус пульсации: треугольная волна — от минимума к максимуму и обратно */
    const triangleProgress = pulseProgress < 0.5 ? pulseProgress * 2 : (1 - pulseProgress) * 2;
    const pulseRadius = minPulse + (maxPulse - minPulse) * triangleProgress;
    /** Прозрачность круга изменяется от 0.2 (min) до 0.4 (max) синхронно с радиусом */
    const alpha = 0.2 + 0.2 * triangleProgress;

    this.canvasContext.save();
    this.canvasContext.beginPath();
    /**
     * Рисуем круг с центром в точке (pointX, pointY + 0.3) и радиусом pulseRadius
     * 0.3 - это смещение по Y, чтобы пульсация была ровно по центру точки
     */
    this.canvasContext.arc(pointX * dpr, (pointY - 0.3) * dpr, pulseRadius, 0, 2 * Math.PI);
    /** Заливаем круг цветом с прозрачностью alpha */
    this.canvasContext.fillStyle = `rgb(${glowPulsedColor}, ${alpha.toFixed(2)})`;
    this.canvasContext.fill();
    this.canvasContext.closePath();
    this.canvasContext.restore();
  }

  /**
   * Отрисовка пульсирующих точек на канвасе
   * @param tilePixelCoords - координаты тайла в пикселях
   * @param getFeatureTileCoords - функция для получения координат точки на тайле
   * @param canvasContext - контекст канваса, на котором рисуются точки
   * @param drawPoint - функция для отрисовки точки
   * @param pulseProgress - прогресс пульсации от 0 до 1
   */
  public drawTileWithPulse({
    tilePixelCoords,
    getFeatureTileCoords,
    canvasContext,
    drawPoint,
    pulseProgress,
    isDetailed,
    isResultable,
    showOnlyPrice,
    pointType,
  }: TDrawTileWithPulse) {
    if (!canvasContext) {
      return;
    }
    canvasContext.clearRect(0, 0, canvasContext.canvas.width, canvasContext.canvas.height);
    this.features.forEach(feature => {
      const coords = getFeatureTileCoords(feature, tilePixelCoords, this.tileZoom);

      const config = getPointConfig({
        feature,
        isResidentialComplexesListing: false,
        isDetailed,
        isResultable,
        showOnlyPrice,
        pointType,
      });

      this.drawPulse({
        pointX: coords[0],
        pointY: coords[1],
        pulseProgress: this.shouldPulse(feature) ? pulseProgress : undefined,
        config,
      });

      drawPoint({
        pointCoords: coords,
        ...config,
      });
    });
  }
}
